import { useEffect, useState } from 'react'
import { useMetrics }                from '../hooks/useMetrics'
import { PostStatusChart }           from '../components/dashboard/PostStatusChart'
import { TopPostsTable }             from '../components/dashboard/TopPostsTable'
import { EmailLogsModal }            from '../components/dashboard/EmailLogsModal'
import { PostCountryViewsSection }   from '../components/dashboard/PostCountryViewsSection'
import { Spinner }                   from '../components/ui/Spinner'

const KPI_CONFIG = [
  { key: 'totalPosts',        label: 'Total Posts',    icon: '📄', variant: 'default'  as const, grad: 'linear-gradient(135deg,#334155,#475569)' },
  { key: 'publishedPosts',    label: 'Published',      icon: '✅', variant: 'success'  as const, grad: 'linear-gradient(135deg,#15803d,#16a34a)' },
  { key: 'draftPosts',        label: 'Drafts',         icon: '✏️', variant: 'warning'  as const, grad: 'linear-gradient(135deg,#b45309,#d97706)' },
  { key: 'totalComments',     label: 'Comments',       icon: '💬', variant: 'default'  as const, grad: 'linear-gradient(135deg,#475569,#64748b)' },
  { key: 'activeSubscribers', label: 'Subscribers',    icon: '⭐', variant: 'accent'   as const, grad: 'linear-gradient(135deg,#0369a1,#0284c7)' },
  { key: 'totalPageViews',    label: 'Total Views',    icon: '👁️', variant: 'accent'   as const, grad: 'linear-gradient(135deg,#4338ca,#6366f1)' },
]

type MetricsKey = 'totalPosts' | 'publishedPosts' | 'draftPosts' | 'totalComments' | 'activeSubscribers' | 'totalPageViews'

export function DashboardPage() {
  const { metrics, loading, fetch } = useMetrics()
  const [emailLogsOpen, setEmailLogsOpen] = useState(false)

  useEffect(() => { fetch() }, [fetch])

  return (
    <>
      <div className="dash-page-header">
        <div className="dash-page-header-text">
          <h1 className="dash-page-title">Dashboard</h1>
          <p className="dash-page-sub">Overview of your blog's content and activity.</p>
        </div>
        <div className="dash-page-header-badge">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {loading && !metrics ? (
        <div className="dash-loading"><Spinner label="Loading metrics…" /></div>
      ) : metrics ? (
        <div className="dash-body">

          {/* ── KPI row ─────────────────────────────────────────── */}
          <div className="dash-kpi-grid">
            {KPI_CONFIG.map(cfg => {
              const val = metrics[cfg.key as MetricsKey]
              return (
                <div key={cfg.key} className="kpi-card">
                  <div className="kpi-icon-wrap" style={{ background: cfg.grad }}>
                    <span className="kpi-icon">{cfg.icon}</span>
                  </div>
                  <div className="kpi-body">
                    <span className="kpi-label">{cfg.label}</span>
                    <span className="kpi-value">{(val as number).toLocaleString()}</span>
                  </div>
                  <div className="kpi-bar" style={{ background: cfg.grad }} />
                </div>
              )
            })}
          </div>

          {/* ── Chart + pending subscribers ─────────────────────── */}
          <div className="dash-charts">
            <PostStatusChart metrics={metrics} />

            <div className="pend-card">
              <div className="pend-head">
                <span className="pend-icon">⌛</span>
                <div>
                  <h3 className="pend-title">Pending Subscribers</h3>
                  <p className="pend-sub">Awaiting confirmation</p>
                </div>
              </div>
              <div className="pend-value">{metrics.pendingSubscribers.toLocaleString()}</div>
              <div className="pend-footer">
                <span className="pend-footer-stat">
                  <span className="pend-dot pend-dot--green" />
                  {metrics.activeSubscribers.toLocaleString()} active
                </span>
                <span className="pend-footer-stat">
                  <span className="pend-dot pend-dot--amber" />
                  {metrics.pendingSubscribers.toLocaleString()} pending
                </span>
              </div>
            </div>
          </div>

          {/* ── Top posts tables ─────────────────────────────────── */}
          <div className="dash-tables">
            <TopPostsTable posts={metrics.topPostsByViews} title="Top Posts by Views" />
            <TopPostsTable posts={metrics.recentPosts}     title="Recently Published" />
          </div>

          {/* ── Email delivery ───────────────────────────────────── */}
          {metrics.emailStats && (
            <div
              className="email-card"
              onClick={() => setEmailLogsOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setEmailLogsOpen(true)}
              title="Click to view full email log"
            >
              <div className="email-head">
                <div className="email-head-left">
                  <span className="email-head-icon">✉️</span>
                  <div>
                    <h2 className="email-head-title">Email Delivery</h2>
                    <p className="email-head-sub">Outbound newsletter and notification stats</p>
                  </div>
                </div>
                <span className="email-view-logs">View logs →</span>
              </div>

              <div className="email-stats">
                {[
                  { label: 'Total Sent', val: metrics.emailStats.totalSent,  color: '#94a3b8', icon: '📤' },
                  { label: 'Delivered',  val: metrics.emailStats.delivered,  color: '#16a34a', icon: '✅' },
                  { label: 'Pending',    val: metrics.emailStats.pending,    color: '#d97706', icon: '⏳' },
                  { label: 'Failed',     val: metrics.emailStats.failed,     color: '#dc2626', icon: '❌' },
                ].map(s => (
                  <div key={s.label} className="email-stat">
                    <span className="email-stat-icon">{s.icon}</span>
                    <span className="email-stat-val" style={{ color: s.color }}>
                      {s.val.toLocaleString()}
                    </span>
                    <span className="email-stat-lbl">{s.label}</span>
                    {metrics.emailStats.totalSent > 0 && (
                      <div className="email-stat-bar-track">
                        <div
                          className="email-stat-bar-fill"
                          style={{
                            width: `${Math.round((s.val / metrics.emailStats.totalSent) * 100)}%`,
                            background: s.color,
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Geo sections ─────────────────────────────────────── */}
          <PostCountryViewsSection
            countryPostCounts={metrics.countryPostCounts ?? []}
            topCountries={metrics.topCountries ?? []}
          />

        </div>
      ) : null}

      <EmailLogsModal open={emailLogsOpen} onClose={() => setEmailLogsOpen(false)} />

      <style>{`
        /* ── Page header ───────────────────────────────────────── */
        .dash-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 28px;
          flex-wrap: wrap;
        }
        .dash-page-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--text-primary);
          margin: 0 0 4px;
          letter-spacing: -0.02em;
        }
        .dash-page-sub { font-size: 0.85rem; color: var(--text-muted); margin: 0; }
        .dash-page-header-badge {
          font-size: 0.78rem;
          color: var(--text-muted);
          background: var(--bg-secondary);
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: 5px 14px;
          font-weight: 500;
        }

        /* ── Loading ───────────────────────────────────────────── */
        .dash-loading { display: flex; justify-content: center; padding: 80px 0; }

        /* ── Body ──────────────────────────────────────────────── */
        .dash-body { display: flex; flex-direction: column; gap: 24px; }

        /* ── KPI grid ──────────────────────────────────────────── */
        .dash-kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 14px;
        }
        .kpi-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 0;
          display: flex;
          align-items: center;
          gap: 0;
          overflow: hidden;
          position: relative;
          box-shadow: 0 1px 3px rgba(0,0,0,0.07);
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .kpi-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.12); transform: translateY(-1px); }
        .kpi-icon-wrap {
          width: 56px;
          min-height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .kpi-icon { font-size: 1.4rem; }
        .kpi-body {
          flex: 1;
          padding: 14px 14px 14px 12px;
          display: flex;
          flex-direction: column;
          gap: 3px;
          min-width: 0;
        }
        .kpi-label {
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          color: var(--text-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .kpi-value {
          font-size: 1.75rem;
          font-weight: 800;
          color: var(--text-primary);
          line-height: 1;
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.02em;
        }
        .kpi-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2.5px;
        }

        /* ── Charts row ────────────────────────────────────────── */
        .dash-charts {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 16px;
        }

        /* ── Pending subscribers card ──────────────────────────── */
        .pend-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          display: flex;
          flex-direction: column;
        }
        .pend-head {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .pend-icon  { font-size: 1.4rem; flex-shrink: 0; }
        .pend-title { font-size: 0.9rem; font-weight: 700; color: #fff; margin: 0 0 2px; }
        .pend-sub   { font-size: 0.72rem; color: rgba(255,255,255,0.72); margin: 0; }
        .pend-value {
          font-size: 3.5rem;
          font-weight: 800;
          color: var(--text-primary);
          line-height: 1;
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.03em;
          padding: 20px 20px 8px;
          flex: 1;
        }
        .pend-footer {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 8px 20px 16px;
          border-top: 1px solid var(--border);
          margin-top: auto;
        }
        .pend-footer-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.78rem;
          color: var(--text-secondary);
        }
        .pend-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .pend-dot--green { background: #16a34a; }
        .pend-dot--amber { background: #d97706; }

        /* ── Tables row ────────────────────────────────────────── */
        .dash-tables {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        /* ── Email card ────────────────────────────────────────── */
        .email-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          cursor: pointer;
          transition: box-shadow 0.2s, border-color 0.2s;
        }
        .email-card:hover {
          border-color: #ea580c;
          box-shadow: 0 4px 20px rgba(234,88,12,0.15);
        }
        .email-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          gap: 10px;
          background: linear-gradient(135deg, #c2410c 0%, #ea580c 100%);
          border-bottom: 1px solid rgba(255,255,255,0.08);
          flex-wrap: wrap;
        }
        .email-head-left   { display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1; }
        .email-head-icon   { font-size: 1.3rem; flex-shrink: 0; }
        .email-head-title  { font-size: 0.88rem; font-weight: 700; color: #fff; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .email-head-sub    { font-size: 0.7rem; color: rgba(255,255,255,0.72); margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .email-view-logs {
          font-size: 0.75rem;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.25);
          border-radius: 20px;
          padding: 4px 12px;
          white-space: nowrap;
          flex-shrink: 0;
          transition: background 0.15s;
        }
        .email-card:hover .email-view-logs { background: rgba(255,255,255,0.25); }

        .email-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
        }
        .email-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 20px 16px 16px;
          border-right: 1px solid var(--border);
          text-align: center;
        }
        .email-stat:last-child { border-right: none; }
        .email-stat-icon { font-size: 1.2rem; margin-bottom: 2px; }
        .email-stat-val  { font-size: 1.8rem; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; letter-spacing: -0.02em; }
        .email-stat-lbl  { font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); }
        .email-stat-bar-track {
          width: 100%;
          height: 4px;
          background: var(--border);
          border-radius: 2px;
          overflow: hidden;
          margin-top: 6px;
        }
        .email-stat-bar-fill { height: 100%; border-radius: 2px; transition: width 0.4s ease; min-width: 2px; }

        /* ── Responsive ────────────────────────────────────────── */
        @media (max-width: 900px) {
          .dash-charts, .dash-tables { grid-template-columns: 1fr; }
          .email-stats { grid-template-columns: repeat(2, 1fr); }
          .email-stat:nth-child(2) { border-right: none; }
          .email-stat:nth-child(1),
          .email-stat:nth-child(2) { border-bottom: 1px solid var(--border); }
        }
        @media (max-width: 560px) {
          .dash-kpi-grid { grid-template-columns: repeat(2, 1fr); }
          .email-stats   { grid-template-columns: repeat(2, 1fr); }
          .email-stat:nth-child(2) { border-right: none; }
          .email-stat:nth-child(1),
          .email-stat:nth-child(2) { border-bottom: 1px solid var(--border); }
          .kpi-icon-wrap { width: 46px; min-height: 72px; }
          .kpi-icon      { font-size: 1.2rem; }
          .kpi-value     { font-size: 1.5rem; }
          .kpi-body      { padding: 10px 10px 10px 8px; }
          .email-stat    { padding: 14px 10px 12px; }
          .email-stat-val { font-size: 1.5rem; }
          .dash-page-header-badge { display: none; }
        }
      `}</style>
    </>
  )
}
